import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Play, ArrowRight, Star, Sparkles, Utensils, Monitor, Moon } from 'lucide-react';
import { toast } from 'react-toastify';

interface ActivityCard {
  id: string;
  title: string;
  description: string;
  duration: string;
  category: 'physical' | 'mental' | 'financial' | 'spiritual' | 'nutritional' | 'emotio-psycho-rational' | 'legendarial' | 'occupational' | 'religio-traditio-cultural' | 'socio-relational' | 'environmental';
  image: string;
  icon: React.ReactNode;
  featured?: boolean;
  consultantId?: string;
}

const WellnessLoopPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['All']);
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Normalize category to match filter format
  const normalizeCategory = (category: string): ActivityCard['category'] => {
    const categoryMap: { [key: string]: ActivityCard['category'] } = {
      'physical': 'physical',
      'financial': 'financial',
      'spiritual': 'spiritual',
      'nutritional': 'nutritional',
      'emotio-psycho-rational': 'emotio-psycho-rational',
      'emotio_psycho_rational': 'emotio-psycho-rational',
      'legendarial': 'legendarial',
      'occupational': 'occupational',
      'religio-traditio-cultural': 'religio-traditio-cultural',
      'religio_traditio_cultural': 'religio-traditio-cultural',
      'socio-relational': 'socio-relational',
      'socio_relational': 'socio-relational',
      'environmental': 'environmental'
    };

    const lowerCategory = (category || '').toLowerCase().replace(/_/g, '-').trim();
    return categoryMap[lowerCategory] || 'physical'; // Default to physical if unknown
  };

  // Fetch consultant sessions from user's groups
  useEffect(() => {
    const fetchWellnessSessions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const allSessions: ActivityCard[] = [];
        const sessionIds = new Set<string>(); // To avoid duplicates

        // Add default system-wide wellness sessions
        const defaultSessions: ActivityCard[] = [
          {
            id: 'yoga',
            title: 'Morning Yoga Flow',
            description: 'Start your day with a gentle flow to awaken your body and mind, setting a positive tone for the hours ahead.',
            duration: '10 min',
            category: 'physical',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
            icon: <Star className="wellness-loop__featured-icon" />,
            featured: true
          },
          {
            id: 'meditation',
            title: 'Mindful Meditation',
            description: 'Find inner peace and clarity with our guided meditation sessions. Perfect for beginners and experts alike.',
            duration: '15 min',
            category: 'spiritual',
            image: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
            icon: <Sparkles className="wellness-loop__activity-icon" />
          },
          {
            id: 'nutrition',
            title: 'Healthy Eating Habits',
            description: 'Nourish your body from the inside out with delicious, balanced meal plans and nutritional advice.',
            duration: '20 min',
            category: 'nutritional',
            image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
            icon: <Utensils className="wellness-loop__activity-icon" />
          },
          {
            id: 'financial',
            title: 'Financial Planning',
            description: 'Secure your future and reduce financial stress with smart strategies and expert budgeting tips.',
            duration: '25 min',
            category: 'financial',
            image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
            icon: <Monitor className="wellness-loop__activity-icon" />
          },
          {
            id: 'sleep',
            title: 'Sleep Well Rituals',
            description: 'Improve your sleep quality and wake up refreshed with proven relaxation techniques and routines.',
            duration: '12 min',
            category: 'emotio-psycho-rational',
            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
            icon: <Moon className="wellness-loop__activity-icon" />
          }
        ];

        defaultSessions.forEach(session => {
          allSessions.push(session);
          sessionIds.add(session.id);
        });

        // Fetch user's groups (if any) to get consultant sessions and modules
        if (user?.id) {
          try {
            const groupsResponse = await fetch('/api/groups/joined?page=1&limit=100', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            });

            if (groupsResponse.ok) {
              const groupsData = await groupsResponse.json();
              const userGroups = groupsData.data?.groups || [];

              // Fetch sessions and modules for each group's consultant
              for (const group of userGroups) {
                if (group.createdBy) {
                  try {
                    // Fetch both modules and sessions from the consultant
                    const [modulesResponse, sessionsResponse] = await Promise.all([
                      fetch(
                        `/api/wellness-modules/consultant/${group.createdBy}`,
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          credentials: 'include'
                        }
                      ),
                      fetch(
                        `/api/wellness-sessions/consultant/${group.createdBy}`,
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          credentials: 'include'
                        }
                      )
                    ]);

                    const categoryIcons: { [key: string]: React.ReactNode } = {
                      physical: <Sparkles className="wellness-loop__activity-icon" />,
                      spiritual: <Moon className="wellness-loop__activity-icon" />,
                      financial: <Monitor className="wellness-loop__activity-icon" />,
                      nutritional: <Utensils className="wellness-loop__activity-icon" />,
                      'emotio-psycho-rational': <Sparkles className="wellness-loop__activity-icon" />,
                      legendarial: <Star className="wellness-loop__activity-icon" />,
                      occupational: <Monitor className="wellness-loop__activity-icon" />,
                      'religio-traditio-cultural': <Sparkles className="wellness-loop__activity-icon" />,
                      'socio-relational': <Sparkles className="wellness-loop__activity-icon" />,
                      environmental: <Sparkles className="wellness-loop__activity-icon" />
                    };

                    // Process modules
                    if (modulesResponse.ok) {
                      const modulesData = await modulesResponse.json();
                      const modules = modulesData.data?.modules || modulesData || [];

                      modules.forEach((module: any) => {
                        if (!sessionIds.has(module._id || module.id)) {
                          const normalizedCategory = normalizeCategory(module.category);
                          allSessions.push({
                            id: module._id || module.id,
                            title: module.title,
                            description: module.description,
                            duration: `${module.estimatedDuration || 0} min`,
                            category: normalizedCategory,
                            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
                            icon: categoryIcons[normalizedCategory] || <Sparkles className="wellness-loop__activity-icon" />,
                            consultantId: group.createdBy
                          });
                          sessionIds.add(module._id || module.id);
                        }
                      });
                    }

                    // Process sessions
                    if (sessionsResponse.ok) {
                      const sessionsData = await sessionsResponse.json();
                      const sessions = sessionsData.data?.sessions || sessionsData || [];

                      sessions.forEach((session: any) => {
                        if (!sessionIds.has(session._id || session.id)) {
                          const normalizedCategory = normalizeCategory(session.category);
                          allSessions.push({
                            id: session._id || session.id,
                            title: session.title,
                            description: session.description,
                            duration: `${session.duration || 0} min`,
                            category: normalizedCategory,
                            image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
                            icon: categoryIcons[normalizedCategory] || <Sparkles className="wellness-loop__activity-icon" />,
                            consultantId: group.createdBy
                          });
                          sessionIds.add(session._id || session.id);
                        }
                      });
                    }
                  } catch (error) {
                    console.error('Error fetching sessions/modules for group:', error);
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error fetching user groups:', error);
          }
        }

        setActivities(allSessions);
      } catch (error) {
        console.error('Error fetching wellness sessions:', error);
        toast.error('Failed to load wellness sessions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWellnessSessions();
  }, [user?.id]);

  // Handler for starting a session
  const handleStartSession = (sessionId: string, consultantId?: string) => {
    // Handle default hardcoded sessions
    if (sessionId === 'sleep') {
      navigate('/panel/dashboard/sleeping-meditation');
    } else if (sessionId === 'meditation') {
      navigate('/panel/dashboard/morning-meditation');
    } else if (sessionId === 'nutrition') {
      navigate('/panel/wellness-loop/healthy-eating-habits');
    } else if (sessionId === 'yoga') {
      navigate('/panel/dashboard/yoga-flow');
    } else if (sessionId === 'financial') {
      navigate('/panel/financial-planning');
    } else if (consultantId) {
      // For consultant-created sessions, navigate to the detail page
      navigate(`/panel/dashboard/wellness-session/${sessionId}`);
    } else {
      toast.info('Starting session...');
    }
  };

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => {
      // If 'All' is clicked
      if (category === 'All') {
        return prev.includes('All') ? [] : ['All'];
      }
      
      // If a specific category is clicked
      const updated = prev.filter(c => c !== 'All'); // Remove 'All' if present
      
      if (updated.includes(category)) {
        // Remove the category
        const newSelection = updated.filter(c => c !== category);
        return newSelection.length === 0 ? ['All'] : newSelection;
      } else {
        // Add the category
        return [...updated, category];
      }
    });
  };

  const filteredActivities = activities.filter(activity => {
    if (selectedCategories.includes('All')) {
      return true;
    }
    const categoryLower = activity.category.toLowerCase();
    return selectedCategories.some(cat => cat.toLowerCase().replace(/\s+/g, '-') === categoryLower.replace(/\s+/g, '-'));
  });

  const featuredActivity = filteredActivities.find(activity => activity.featured) || filteredActivities[0] || null;
  const regularActivities = filteredActivities.filter(activity => !activity.featured);

  if (isLoading) {
    return (
      <div className={`wellness-loop ${isDarkMode ? 'dark' : ''}`}>
        <div className="wellness-loop__container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading wellness sessions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`wellness-loop ${isDarkMode ? 'dark' : ''}`}>
      <div className="wellness-loop__container">
        {/* Header Section */}
        <div className="wellness-loop__header">
          <div className="wellness-loop__header-content">
            <div className="wellness-loop__header-text">
              <h2 className="wellness-loop__title">Today</h2>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="wellness-loop__filters">
          {['All', 'Physical', 'Financial', 'Spiritual', 'Nutritional', 'Emotio-Psycho-Rational', 'Legendarial', 'Occupational', 'Religio-Traditio-Cultural', 'Socio-Relational', 'Environmental'].map((category) => (
            <button
              key={category}
              className={`wellness-loop__filter-button ${
                selectedCategories.includes(category) ? 'wellness-loop__filter-button--active' : ''
              }`}
              data-category={category.toLowerCase().replace(/\s+/g, '-')}
              onClick={() => handleCategoryToggle(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {activities.length === 0 && !isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: isDarkMode ? '#ccc' : '#666' }}>
            <p>No wellness sessions available at this time.</p>
          </div>
        ) : null}

        {/* Featured Activity */}
        {featuredActivity && (
          <div className="wellness-loop__featured">
            <div className="wellness-loop__featured-card">
              <div
                className="wellness-loop__featured-image"
                style={{ backgroundImage: `url(${featuredActivity.image})` }}
              ></div>
              <div className="wellness-loop__featured-overlay"></div>
              <div className="wellness-loop__featured-content">
                <div className="wellness-loop__featured-badge">
                  {featuredActivity.icon}
                  <span>Featured Activity</span>
                </div>
                <h2 className="wellness-loop__featured-title">{featuredActivity.title}</h2>
                <p className="wellness-loop__featured-description">{featuredActivity.description}</p>
                <div className="wellness-loop__featured-footer" style={{marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <span className="wellness-loop__featured-duration">{featuredActivity.duration}</span>
                  <button
                    className="wellness-loop__featured-button"
                    style={{marginLeft: 'auto'}}
                    onClick={() => handleStartSession(featuredActivity.id, featuredActivity.consultantId)}
                  >
                    Start
                    <Play size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activities Grid */}
        <div className="wellness-loop__activities">
          <div className="wellness-loop__activities-grid">
            {regularActivities.map((activity) => (
              <div key={activity.id} className="wellness-loop__activity-card">
                <div
                  className="wellness-loop__activity-image"
                  style={{ backgroundImage: `url(${activity.image})` }}
                ></div>
                <div className="wellness-loop__activity-content">
                  <div className="wellness-loop__activity-header">
                    <div className="wellness-loop__activity-icon-container">
                      {activity.icon}
                    </div>
                    <h3 className="wellness-loop__activity-title">{activity.title}</h3>
                  </div>
                  <p className="wellness-loop__activity-description">{activity.description}</p>
                  <button
                    className="wellness-loop__activity-button"
                    onClick={() => handleStartSession(activity.id, activity.consultantId)}
                  >
                    Start
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessLoopPage;


